import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { readable } from "@/components/app/phone/phone-chrome";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { SettingsScopeLine } from "@/components/app/rails/settings-scope-line";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { FacilityPhoneSettings } from "@/routes/_authenticated/_front-desk/sarah/-components/facility-phone-settings";
import { GiveNumberDialog } from "@/routes/_authenticated/_front-desk/sarah/-components/give-number-dialog";
import { AvailableNumberCard } from "./-components/available-number-card";
import { FacilityNumberCard } from "./-components/facility-number-card";
import { PersonNumberDetail } from "./-components/person-number-detail";
import { holderOf, KIND_BADGE, KIND_LABEL, kindOf } from "./-components/phone-kind";

export const Route = createFileRoute("/_authenticated/_front-desk/sarah/numbers/$numberId")({
  beforeLoad: ({ context }) => guardRoute(context.me, "phone-number-bank"),
  component: PhoneNumberPage,
});

/** One number: a facility's line with its phone settings, a person's company number, or one in the
 * bank. */
function PhoneNumberPage() {
  const { numberId } = Route.useParams();
  const navigate = useNavigate();
  const bank = useQuery(orpc.frontDesk.staff.numberBank.list.queryOptions());
  const canAssign = useCan("settings:manage");
  const [changing, setChanging] = useState(false);
  const back = (
    <Button asChild variant="outline" size="sm">
      <Link to="/sarah/numbers">
        <ArrowLeft className="size-4" />
        All numbers
      </Link>
    </Button>
  );

  if (bank.isPending) return <Skeleton className="h-96 w-full" />;
  if (bank.isError) {
    return (
      <ReadFailed
        message="This number could not be loaded."
        error={bank.error}
        onRetry={() => void bank.refetch()}
        retrying={bank.isFetching}
      />
    );
  }
  const number = bank.data.find((row) => row.id === numberId);
  if (!number) {
    return (
      <>
        <PageHeader title="Phone" actions={back} />
        <EmptyState
          title="Number not found"
          description="It may have been removed from the bank. Every number the organization owns is on the Phone list."
        />
      </>
    );
  }

  const kind = kindOf(number);
  const who = holderOf(number);
  const toList = () => void navigate({ to: "/sarah/numbers" });

  const heldBy = kind === "facility" ? (who ?? undefined) : undefined;

  return (
    <div className="space-y-6">
      <div>
        <SettingsScopeLine
          scope={heldBy ? "facility" : "organization"}
          building={heldBy}
          className="mb-3"
        />
        <PageHeader
          title={
            <span className="flex flex-wrap items-center gap-3">
              <span className="tabular-nums">{readable(number.phoneNumber)}</span>
              <Badge variant={KIND_BADGE[kind]}>{KIND_LABEL[kind]}</Badge>
            </span>
          }
          meta={[who ?? "In the bank", number.label].filter(Boolean).join(", ")}
          actions={back}
        />
      </div>

      {kind === "facility" && number.holder?.facilityId ? (
        <>
          <FacilityNumberCard
            number={number}
            canAssign={canAssign && number.managedHere}
            onChange={() => setChanging(true)}
            onReleased={toList}
          />
          <FacilityPhoneSettings facilityId={number.holder.facilityId} />
        </>
      ) : kind === "staff" ? (
        <PersonNumberDetail number={number} canAssign={canAssign} onReleased={toList} />
      ) : kind === "available" ? (
        <AvailableNumberCard
          number={number}
          canAssign={canAssign}
          onAssign={() => setChanging(true)}
          onRemoved={toList}
        />
      ) : (
        <EmptyState
          title="Nancy's number"
          description="Nancy uses it for store sign-in codes. It is managed from Nancy's settings."
        />
      )}

      {changing ? (
        <GiveNumberDialog number={number} onOpenChange={(open) => !open && setChanging(false)} />
      ) : null}
    </div>
  );
}
